import { visionURL } from "../config/environment";
import Jsona from "jsona";
import JsonApiService from "../services/jsonapiService";
import { useEffect, useState, useCallback } from "react";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  enqueueSuccessSnackbar,
  enqueueErrorSnackbar
} from "../redux/actions/snackbarActions";
import _ from "lodash";
import { useReportPath } from "./utils";
import visionCallService from "../services/visionCallService";

const dataFormatter = new Jsona();

const userAssignmentsService = async (employeeId, sort = "dueDate") =>
  new JsonApiService("user_assignments", {
    baseUrl: `${visionURL}/admin_console/api/administration/user/${employeeId}`,
    credentials: "include",
    sort: sort
  });

const exclusionsService = async employeeId =>
  new JsonApiService("exclusions", {
    baseUrl: `${visionURL}/admin_console/api/administration/user/${employeeId}`,
    credentials: "include"
  });

export const useUserAssignments = (employeeId, rawSortBy, updated, deleted) => {
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const sortBy = rawSortBy
    .map(sort => `${sort.desc ? "-" : ""}${sort.id}`)
    .join(",");

  const load = async () => {
    setIsLoading(true);
    const service = await userAssignmentsService(employeeId, sortBy);
    const data = await (await service.all()).json();
    setIsLoading(false);
    return setData(dataFormatter.deserialize(data));
  };

  const trainingObjectSort = () => {
    setData(data =>
      _.orderBy(
        data,
        ["trainingObject.name"],
        [rawSortBy[0].desc ? "desc" : "asc"]
      )
    );
  };

  const updateSort = (reload = true) => {
    if (rawSortBy[0] && rawSortBy[0].id === "trainingObjects.name")
      trainingObjectSort();
    else if (reload) load();
  };

  // New Data
  useEffect(updateSort, [employeeId, rawSortBy]);

  // Updated data
  useEffect(() => {
    load();
    updateSort(false);
  }, [updated]);

  // Deleted data
  useEffect(() => {
    setData(data => data.filter(datum => !deleted.includes(datum.id)));
  }, [deleted]);

  // Always put excluded at the bottom
  const userAssignments =
    !rawSortBy[0] || rawSortBy[0].id === "dueDate"
      ? _(data)
          .filter(datam => datam.exclusion === null)
          .concat(
            _.sortBy(
              _.reject(data, datam => datam.exclusion === null),
              obj => obj.trainingObject.name.toLowerCase()
            )
          )
          .value()
      : data;
  return { userAssignments, isLoading };
};

const useUserAssignmentNotification = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const notify = status => {
    if (status === 200)
      dispatch(
        enqueueSuccessSnackbar({ message: t("userAssignments.saveSuccess") })
      );
    else
      dispatch(
        enqueueErrorSnackbar({ message: t("userAssignments.saveError") })
      );
  };
  return notify;
};

export const useBulkUserAssignmentSave = (
  employeeId,
  presentUpdateCallback
) => {
  const notify = useUserAssignmentNotification();
  const batchUpdate = async data => {
    const service = await userAssignmentsService(employeeId);
    const response = await service.bulk_update(JSON.stringify({ data: data }));
    notify(response.status);
    if (response.status === 200) presentUpdateCallback(data);
    return response;
  };
  return batchUpdate;
};

export const useBatchDeleteUserAssignments = employeeId => {
  const notify = useUserAssignmentNotification();
  const batchDelete = async data => {
    const service = await userAssignmentsService(employeeId);
    const response = await service.bulk_delete(JSON.stringify({ data: data }));
    notify(response.status);
    return response.json();
  };
  return batchDelete;
};

export const createExclusions = employeeId => {
  const notify = useUserAssignmentNotification();
  const batchCreate = async data => {
    const service = await exclusionsService(employeeId);
    const response = await service.create(JSON.stringify({ data: data }));
    notify(response.status);
    return response;
  };
  return batchCreate;
};

export const useUpdateExclusion = employeeId => {
  const notify = useUserAssignmentNotification();
  const updateExclusion = useCallback(async data => {
    if (data.expires === "false") data.expiresAt = null;
    const service = await exclusionsService(employeeId);
    const response = await service.update(
      employeeId,
      visionCallService.serializeRequestBody(data, "exclusion")
    );
    notify(response.status);
    return response;
  });
  return updateExclusion;
};

export const useDeleteExclusions = (employeeId, presentUpdateCallback) => {
  const notify = useUserAssignmentNotification();
  const deleteExclusion = async data => {
    const deletionPayload = data.map(row => ({
      userId: row.user.id,
      trainingObjectId: row.trainingObject.id
    }));
    const service = await exclusionsService(employeeId);
    const response = await service.bulk_delete(
      JSON.stringify({ data: deletionPayload })
    );
    notify(response.status);
    if (response.status === 200) {
      // Get the recalculated dueDate from the backend
      const user_assignments = (await response.json()).user_assignments.reduce(
        (updates, update) => {
          updates[update.id] = { dueDate: update.attributes.dueDate };
          return updates;
        },
        {}
      );
      // Update to have no exclusion and the new dueDate
      const updates = data.map(row => ({
        id: row.id,
        exclusion: null,
        dueDate: user_assignments[row.id].dueDate
      }));
      presentUpdateCallback(updates);
    }
    return response;
  };
  return deleteExclusion;
};

export const usePrintAssignments = employeeId => {
  const printUrl = `${visionURL}/admin_console/api/administration/user/${employeeId}/user_assignments_print`;
  return useReportPath(printUrl);
};

export const useEmailAssignments = employeeId => {
  const emailUrl = `${visionURL}/admin_console/api/administration/user/${employeeId}/user_assignments_email`;
  return useReportPath(emailUrl);
};

const CANCEL_TYPES = ["Individual", "Validate", "Enrolled"];
const EXCLUDE_TYPES = ["Location", "Position", "Group"];
export const CANCEL = "Cancel";
export const EXCLUDE = "Exclude";
export const CANCELANDEXCLUDE = "Cancel/Exclude";

export const decideExcludeOrCancel = type => {
  if (CANCEL_TYPES.includes(type)) return CANCEL;
  else if (EXCLUDE_TYPES.includes(type)) return EXCLUDE;
};

export const useUserAssignment = () => {
  const [loading, setLoading] = useState(false);
  return {
    getUserAssignment: async (id, employeeId) => {
      setLoading(true);
      const service = await userAssignmentsService(employeeId);
      const response = await service.find(id).then(response => response.json());
      setLoading(false);
      return dataFormatter.deserialize(response);
    },
    loading
  };
};

export const useDeleteAndExclude = (
  employeeId,
  rows,
  presentDeleteCallback,
  presentUpdateCallback
) => {
  const [label, setLabel] = useState("");
  const deleteAssignment = useBatchDeleteUserAssignments(employeeId);
  const batchExclusion = createExclusions(employeeId);
  const [assignmentExcluded, setAssignmentExcluded] = useState(false);

  const cancel = rows.filter(
    row => decideExcludeOrCancel(row.assignmentType.name) === CANCEL
  );
  const exclude = rows.filter(
    row => decideExcludeOrCancel(row.assignmentType.name) === EXCLUDE
  );

  useEffect(() => {
    if (cancel.length && exclude.length) {
      setLabel(CANCELANDEXCLUDE);
    } else if (cancel.length) {
      setLabel(CANCEL);
    } else if (exclude.length) {
      setLabel(EXCLUDE);
    } else {
      setLabel("");
    }
  }, [rows, assignmentExcluded]);

  const formatExclusionData = (exclude, exclusionData) =>
    exclude.map(row => ({
      userId: employeeId,
      trainingObjectId: row.trainingObject.id,
      expiresAt: exclusionData.expires ? exclusionData.expiresAt : null,
      note: exclusionData.comment
    }));

  const call = useCallback(
    async (exclusionData = {}) => {
      let deleteResponse;
      let exclusionResponse;
      if (cancel.length) {
        cancel.forEach(row => row.toggleRowSelected(false));
        deleteResponse = await deleteAssignment(
          cancel.map(row => ({ type: "userAssignments", id: row.id }))
        );
      }

      const exclusions = formatExclusionData(exclude, exclusionData);

      if (exclude.length) {
        const unselectExclusion = async () => {
          exclude.forEach(row => row.toggleRowSelected(false));
          return await batchExclusion(exclusions);
        };

        exclusionResponse = await unselectExclusion();
      }

      // Update the presentation layer
      if (deleteResponse && deleteResponse.message === "success") {
        const updated = deleteResponse.payload;
        const updated_by_id = updated.data.reduce((hash, assignment) => {
          hash[assignment.id] = assignment;
          return hash;
        }, {});
        presentUpdateCallback(dataFormatter.deserialize(updated));
        presentDeleteCallback(
          cancel.filter(row => !updated_by_id[row.id]).map(row => row.id)
        );
      }
      if (exclusionResponse && exclusionResponse.status === 200) {
        presentUpdateCallback(
          exclude.map(row => ({
            id: row.id,
            exclusion: {
              data: {
                attributes: {
                  note: exclusionData.comment,
                  expiresAt: exclusionData.expiresAt
                }
              }
            }
          }))
        );
        setAssignmentExcluded(!assignmentExcluded);
      }

      const response = {
        deleted: deleteResponse ? deleteResponse : null,
        excluded: exclusionResponse ? await exclusionResponse.json() : null
      };
      return response;
    },
    [employeeId, rows]
  );
  return { label: label, call: call };
};
